import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { House, Package, ChartLine, Money, Truck, Storefront, MapPin, Users, Gear, SignOut, List, X, Globe, ChartBar, Wrench, ArrowsClockwise, PackageIcon, Handshake } from '@phosphor-icons/react';

const adminMenuItems = [
  { path: '/dashboard', icon: House, key: 'nav.actionCenter' },
  { path: '/stock', icon: Package, key: 'nav.stock' },
  { path: '/settlement', icon: ChartLine, key: 'nav.settlement' },
  { path: '/finance', icon: Money, key: 'nav.finance' },
  { path: '/create-do', icon: Truck, key: 'nav.createDO' },
  { path: '/partner-dos', icon: Truck, key: 'nav.partnerDOs' },
  { path: '/adjustments', icon: Wrench, key: 'nav.adjustments' },
  { path: '/performance', icon: ChartBar, key: 'nav.performance' },
  { path: '/products', icon: Storefront, key: 'nav.products' },
  { path: '/partners', icon: Handshake, key: 'nav.partners' },
  { path: '/pharmacy-map', icon: MapPin, key: 'nav.pharmacyMap' },
  { path: '/users', icon: Users, key: 'nav.users' },
  { path: '/settings', icon: Gear, key: 'nav.settings' },
];

const financeMenuItems = [
  { path: '/dashboard', icon: House, key: 'nav.actionCenter' },
  { path: '/settlement', icon: ChartLine, key: 'nav.settlement' },
  { path: '/finance', icon: Money, key: 'nav.finance' },
  { path: '/performance', icon: ChartBar, key: 'nav.performance' },
];

const operationsMenuItems = [
  { path: '/stock', icon: Package, key: 'nav.stock' },
  { path: '/create-do', icon: Truck, key: 'nav.createDO' },
  { path: '/partner-dos', icon: Truck, key: 'nav.partnerDOs' },
  { path: '/adjustments', icon: Wrench, key: 'nav.adjustments' },
];

const pharmacyMenuItems = [
  { path: '/pharmacy/report-sale', icon: ChartLine, key: 'nav.reportSale' },
  { path: '/pharmacy/my-stock', icon: Package, key: 'nav.myStock' },
  { path: '/pharmacy/incoming-dos', icon: Truck, key: 'nav.incomingDOs' },
  { path: '/pharmacy/returns', icon: ArrowsClockwise, key: 'nav.returns' },
  { path: '/pharmacy/invoices', icon: Money, key: 'nav.invoices' },
  { path: '/pharmacy/restock', icon: Storefront, key: 'nav.restock' },
];

const partnerMenuItems = [
  { path: '/partner/live-sales', icon: ChartLine, key: 'nav.liveSales' },
  { path: '/partner/statements', icon: Money, key: 'nav.statements' },
  { path: '/partner/where-stock', icon: MapPin, key: 'nav.whereStock' },
  { path: '/partner/direct-dos', icon: Truck, key: 'nav.partnerDOs' },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const menuItems = user?.role === 'admin'
    ? adminMenuItems
    : user?.role === 'finance'
    ? financeMenuItems
    : user?.role === 'operations'
    ? operationsMenuItems
    : user?.role === 'pharmacy'
    ? pharmacyMenuItems
    : partnerMenuItems;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: '#F8FAFC', fontFamily: 'IBM Plex Sans' }}>
      <button
        data-testid="mobile-menu-toggle"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="lg:hidden fixed top-4 right-4 z-50 bg-white p-2 rounded-lg shadow-md border border-slate-200"
      >
        {mobileMenuOpen ? <X size={24} /> : <List size={24} />}
      </button>

      <aside
        data-testid="sidebar"
        className={`fixed lg:relative inset-y-0 left-0 w-64 bg-white border-r border-slate-200 transform ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} transition-transform duration-200 z-40 flex flex-col`}
      >
        <div className="p-6 border-b border-slate-200">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>
            OctoBoots
          </h1>
          <p className="text-xs text-slate-500 mt-1 uppercase tracking-widest">{user?.role}</p>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace(/\//g, '-')}`}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
                  isActive
                    ? 'bg-blue-50 text-blue-900'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
                <span>{t(item.key)}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-200 space-y-2">
          <button
            data-testid="language-switcher"
            onClick={() => changeLanguage(language === 'en' ? 'zh' : 'en')}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg"
          >
            <Globe size={18} />
            <span>{language === 'en' ? '中文' : 'English'}</span>
          </button>
          <div className="px-3 py-2 border-t border-slate-100 pt-3">
            <p className="text-xs text-slate-500 truncate">{user?.email || user?.phone}</p>
            <p className="text-sm font-medium truncate">{user?.name}</p>
          </div>
          <Button
            data-testid="logout-btn"
            onClick={handleLogout}
            variant="outline"
            className="w-full flex items-center gap-2"
          >
            <SignOut size={18} />
            <span>{t('auth.logout')}</span>
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto lg:ml-0">
        {children}
      </main>
    </div>
  );
};
